import type { TableDataRow } from '@zdc/sdk';
import type { ListFilter } from '@/types/formProperties';
import { getSDK } from '@/store/sdkStore';
import { useCenterStore } from '@/store/centerStore';
import { readThrough } from '@/lib/offlineCache';
import { isNotFoundError, isPermissionDeniedError, logSwallowed } from '@/lib/errors';

export const APPOINTMENT_DOCUMENT = 'medic_encounters';
export const PATIENT_CATALOG = 'medic_patients';
export const PSY_CONCLUSIONS = 'psy_conclusions';
export const PSY_NOTES = 'psy_notes';
export const PSY_ASSIGNMENTS = 'psy_assignments';

export const ENCOUNTER_STATUS_ENUM = 'medic_encounter_status';
export const ENCOUNTER_TYPE_ENUM = 'medic_encounter_type';

export type AppointmentPeriod = 'today' | 'upcoming' | 'past';

export interface CabinetAppointment {
  id: string;
  number: string;
  appointmentAt: string;
  patientId: string;
  patientName: string;
  doctorId: string;
  doctorName: string;
  typeCode: string;
  statusCode: string;
  complaints: string;
  conclusion: string;
  raw: Record<string, unknown>;
}

export interface AppointmentCounts {
  today: number;
  upcoming: number;
  past: number;
  total: number;
}

export interface RelatedConclusion {
  id: string;
  date: string;
  text: string;
  recommendations: string;
  icdCode: string;
  sessionId: string;
}

export interface RelatedNote {
  id: string;
  date: string;
  typeCode: string;
  text: string;
  sessionId: string;
}

export interface RelatedAssignment {
  id: string;
  methodCode: string;
  date: string;
  status: string;
}

export interface AppointmentLinks {
  conclusions: RelatedConclusion[];
  notes: RelatedNote[];
  assignments: RelatedAssignment[];
  denied: boolean;
  missing: string[];
  warnings: string[];
}

export interface CabinetMedicAvailability {
  core: boolean;
  psy: boolean;
}

export interface AppointmentEnumLabels {
  status: Record<string, string>;
  type: Record<string, string>;
}

type Row = Record<string, string> & { id: string };

function slug(): string {
  return useCenterStore.getState().baseSlug || 'local';
}

function cellsOf(row: TableDataRow): Record<string, string> {
  const out: Record<string, string> = {};
  for (const v of row.values) out[String(v.column)] = v.value === null || v.value === undefined ? '' : String(v.value);
  return out;
}

function rowsOf(rows: TableDataRow[]): Row[] {
  return rows.map((row) => ({ id: String(row.id || ''), ...cellsOf(row) }));
}

function toAppointment(row: Row): CabinetAppointment {
  return {
    id: row.id,
    number: row.number || '',
    appointmentAt: row.appointment_at || '',
    patientId: row.patient_id || '',
    patientName: row.patient_id_presentation || '',
    doctorId: row.doctor_id || '',
    doctorName: row.doctor_id_presentation || '',
    typeCode: row.encounter_type || '',
    statusCode: row.status || '',
    complaints: row.complaints || '',
    conclusion: row.conclusion || '',
    raw: row,
  };
}

export function appointmentFromListItem(row: Record<string, unknown>): CabinetAppointment {
  const raw: Row = { id: String(row.id || '') };
  for (const [key, value] of Object.entries(row)) {
    if (value === null || value === undefined) raw[key] = '';
    else raw[key] = String(value);
  }
  return toAppointment(raw);
}

function toConclusion(row: Row): RelatedConclusion {
  return {
    id: row.id,
    date: row.conclusion_date || '',
    text: row.conclusion_text || '',
    recommendations: row.recommendations || '',
    icdCode: row.icd_code || '',
    sessionId: row.session_id || '',
  };
}

function toNote(row: Row): RelatedNote {
  return {
    id: row.id,
    date: row.note_date || '',
    typeCode: row.note_type || '',
    text: row.note_text || '',
    sessionId: row.session_id || '',
  };
}

function toAssignment(row: Row): RelatedAssignment {
  return {
    id: row.id,
    methodCode: row.method_code || '',
    date: row.assigned_date || '',
    status: row.status || 'assigned',
  };
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function localDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function localDateTime(d: Date): string {
  return `${localDate(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function localDayBounds(now: Date): { start: string; end: string } {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { start: localDateTime(start), end: localDateTime(end) };
}

export function countAppointments(appointments: CabinetAppointment[], now = new Date()): AppointmentCounts {
  const counts: AppointmentCounts = { today: 0, upcoming: 0, past: 0, total: appointments.length };
  const day = localDayBounds(now);
  const nowValue = localDateTime(now);
  for (const appointment of appointments) {
    const value = String(appointment.appointmentAt || '');
    if (value >= day.start && value < day.end) counts.today += 1;
    if (value >= nowValue) counts.upcoming += 1;
    else counts.past += 1;
  }
  return counts;
}

export function appointmentPeriodFilters(
  period: AppointmentPeriod,
  patientId?: string,
  now = new Date(),
): ListFilter[] {
  const filters: ListFilter[] = [];
  const day = localDayBounds(now);

  if (period === 'today') {
    filters.push({ field: 'appointment_at', condition: '>=', value: day.start });
    filters.push({ field: 'appointment_at', condition: '<', value: day.end });
  } else if (period === 'upcoming') {
    filters.push({ field: 'appointment_at', condition: '>=', value: localDateTime(now) });
  } else {
    filters.push({ field: 'appointment_at', condition: '<', value: localDateTime(now) });
  }

  if (patientId) filters.push({ field: 'patient_id', condition: '=', value: patientId });
  return filters;
}

export async function loadAppointments(patientId?: string): Promise<CabinetAppointment[]> {
  const key = `cabinet:appointments:${slug()}:${patientId || 'all'}`;
  const result = await readThrough(key, async () => {
    const response = await getSDK().metadata.listTableData({
      schema: 'public',
      table: APPOINTMENT_DOCUMENT,
      pageSize: 1000,
      page: 0,
      filters: patientId ? [{ column: 'patient_id', value: patientId }] : [],
    });
    return rowsOf(response.rows).map(toAppointment).filter((a) => a.id !== '');
  });
  return result.data;
}

export async function loadCabinetAvailability(): Promise<CabinetMedicAvailability> {
  const result = await readThrough(`cabinet:medic-availability:${slug()}`, async () => {
    const sdk = getSDK();
    const [documents, catalogs] = await Promise.all([
      sdk.metadata.listDocuments({}),
      sdk.metadata.listCatalogs({}),
    ]);
    const core = documents.items.some((d) => String(d.code) === APPOINTMENT_DOCUMENT)
      && catalogs.items.some((c) => String(c.code) === PATIENT_CATALOG);
    const psy = catalogs.items.some((c) => [PSY_CONCLUSIONS, PSY_NOTES, PSY_ASSIGNMENTS].includes(String(c.code)));
    return { core, psy };
  });
  return result.data;
}

async function loadEnumLabels(code: string): Promise<Record<string, string>> {
  try {
    const response = await getSDK().metadata.getEnum({ code });
    const labels: Record<string, string> = {};
    for (const item of response.values || []) {
      labels[String(item.value)] = String(item.synonym || item.name || item.value);
    }
    return labels;
  } catch (e) {
    if (!isNotFoundError(e)) logSwallowed(e, `Записи: подписи перечисления ${code}`);
    return {};
  }
}

export async function loadAppointmentEnumLabels(): Promise<AppointmentEnumLabels> {
  const result = await readThrough(`cabinet:appointment-enums:${slug()}`, async () => {
    const [status, type] = await Promise.all([
      loadEnumLabels(ENCOUNTER_STATUS_ENUM),
      loadEnumLabels(ENCOUNTER_TYPE_ENUM),
    ]);
    return { status, type };
  });
  return result.data;
}

async function queryRows(table: string, filters: Array<{ column: string; value: string }>): Promise<Row[]> {
  const response = await getSDK().metadata.listTableData({
    schema: 'public',
    table,
    pageSize: 500,
    page: 0,
    filters,
  });
  return rowsOf(response.rows);
}

function collectError(
  error: unknown,
  source: string,
  state: AppointmentLinks,
): void {
  if (isPermissionDeniedError(error)) {
    state.denied = true;
    state.warnings.push(`${source}: нет прав чтения`);
    return;
  }
  if (isNotFoundError(error)) {
    state.missing.push(source);
    return;
  }
  logSwallowed(error, `Записи: связанные данные ${source}`);
  state.warnings.push(`${source}: не удалось загрузить`);
}

export async function loadAppointmentLinks(appointment: CabinetAppointment): Promise<AppointmentLinks> {
  const key = `cabinet:links:${slug()}:${appointment.id}:${appointment.patientId}`;
  const result = await readThrough(key, async () => {
    const state: AppointmentLinks = {
      conclusions: [],
      notes: [],
      assignments: [],
      denied: false,
      missing: [],
      warnings: [],
    };
    if (!appointment.patientId) return state;

    try {
      const rows = await queryRows(PSY_CONCLUSIONS, [{ column: 'patient_id', value: appointment.patientId }]);
      state.conclusions = rows
        .map(toConclusion)
        .sort((a, b) => String(b.date).localeCompare(String(a.date)));
    } catch (e) {
      collectError(e, 'заключения', state);
    }

    try {
      let rows: Row[] = [];
      if (appointment.id) {
        rows = await queryRows(PSY_NOTES, [{ column: 'session_id', value: appointment.id }]);
      }
      if (rows.length === 0) {
        rows = await queryRows(PSY_NOTES, [{ column: 'patient_id', value: appointment.patientId }]);
      }
      state.notes = rows
        .map(toNote)
        .sort((a, b) => String(b.date).localeCompare(String(a.date)));
    } catch (e) {
      collectError(e, 'заметки', state);
    }

    try {
      const rows = await queryRows(PSY_ASSIGNMENTS, [{ column: 'patient_id', value: appointment.patientId }]);
      state.assignments = rows.map(toAssignment);
    } catch (e) {
      collectError(e, 'назначения тестов', state);
    }

    return state;
  });
  return result.data;
}

export function statusToneClass(code: string): string {
  switch (String(code || '').toLowerCase()) {
    case 'done':
      return 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30';
    case 'cancelled':
      return 'bg-destructive/10 text-destructive border-destructive/30';
    case 'planned':
      return 'bg-primary/10 text-primary border-primary/30';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

export function appointmentInitialData(
  patient?: { id: string; name: string } | null,
  doctorId = '',
): Record<string, unknown> {
  return {
    patient_id: patient?.id || '',
    doctor_id: doctorId,
    encounter_type: 'med_visit',
    appointment_at: new Date().toISOString().slice(0, 19),
    status: 'planned',
  };
}
